import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';

import {
  ACCEPT_SESSION_TRANSFER,
  ACCEPT_SESSION_TRANSFER_EMPTY,
  REJECT_SESSION_TRANSFER,
  REJECT_SESSION_TRANSFER_EMPTY,
} from '../../../store/actions';

const SessionAcceptReject = ({ onClose, transferId, unblockIncomingSession, remainingTime, stopModalTimer }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  /* ---------------------------- REDUX STATES ---------------------------- */

  const { acceptSessionTransfer, acceptSessionTransferLoading, rejectSessionTransfer, rejectSessionTransferLoading, incomingSessionData } =
    useSelector(state => ({
      acceptSessionTransfer: state?.AcceptSessionTransfer?.data,
      acceptSessionTransferLoading: state?.AcceptSessionTransfer?.loading,
      rejectSessionTransfer: state?.RejectSessionTransfer?.data,
      rejectSessionTransferLoading: state?.RejectSessionTransfer?.loading,
      incomingSessionData: state?.IncomingSession?.data,
    }));

  const isLoading = acceptSessionTransferLoading || rejectSessionTransferLoading;

  /* ---------------------------- ACTIONS ---------------------------- */

  const handleAccept = () => {
    dispatch({
      type: ACCEPT_SESSION_TRANSFER,
      payload: {
        data: { transferId },
      },
    });
  };

  const handleReject = () => {
    dispatch({
      type: REJECT_SESSION_TRANSFER,
      payload: {
        data: { transferId },
      },
    });
  };

  /* ---------------------------- SUCCESS HANDLING ---------------------------- */

  useEffect(() => {
    if (!acceptSessionTransfer && !rejectSessionTransfer) return;

    if (acceptSessionTransfer) {
      const ticketId = incomingSessionData?.data?.ticket_id;

      if (!localStorage.getItem('ticketId')) {
        localStorage.setItem('ticketId', ticketId);

        dispatch({ type: ACCEPT_SESSION_TRANSFER_EMPTY });

        history.push({
          pathname: '/v2/customer-manual-search',
          search: `?${queryString.stringify({
            incomingTicketId: ticketId,
          })}`,
        });
      }
    }

    if (rejectSessionTransfer) {
      dispatch({ type: REJECT_SESSION_TRANSFER_EMPTY });
    }

    stopModalTimer();
    unblockIncomingSession();
    onClose();
  }, [acceptSessionTransfer, rejectSessionTransfer, dispatch, history, incomingSessionData, unblockIncomingSession, onClose]);

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  /* ---------------------------- UI ---------------------------- */

  return (
    <Modal isOpen centered size="md" backdrop="static" keyboard={!isLoading}>
      <ModalHeader toggle={onClose} className="border-0 pb-0">
        Session Transferred
      </ModalHeader>

      <ModalBody className="p-4">
        {/* Timer */}
        <div>
          <span className="fw-semibold fs-14 text-danger">Time Remaining: {formatTime(remainingTime)}</span>
        </div>

        {/* Title */}
        <p className="mb-4" style={{ fontSize: '16px' }}>
          Feel free to either accept or decline this session.
        </p>

        {/* Transferred By */}
        <div className="mb-3">
          <h6 className="fw-semibold mb-2">Transferred By</h6>

          <div
            className="d-flex align-items-center p-2"
            style={{
              backgroundColor: '#DEDFE7',
              borderRadius: '8px',
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: '#4DB6AC',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {incomingSessionData?.data?.from_agent_name?.charAt(0)?.toUpperCase()}
            </div>

            <span className="fs-5 fw-semibold" style={{ color: '#6F7594' }}>
              {incomingSessionData?.data?.from_agent_name}
            </span>
          </div>
        </div>

        {/* Reason */}
        <div className="mb-4">
          <h6 className="fw-semibold mb-2">Reason</h6>

          <div
            className="p-3 fw-normal"
            style={{
              backgroundColor: '#DEDFE7',
              borderRadius: '8px',
              color: '#6F7594',
            }}
          >
            {incomingSessionData?.data?.reason}
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex justify-content-between gap-3">
          {/* Reject */}
          <Button
            outline
            color="primary"
            block
            disabled={isLoading}
            onClick={handleReject}
            style={{
              height: '48px',
              fontSize: '16px',
              borderWidth: '2px',
            }}
          >
            {rejectSessionTransferLoading ? <Spinner size="sm" /> : 'Reject'}
          </Button>

          {/* Accept */}
          <Button
            color="primary"
            block
            disabled={isLoading}
            onClick={handleAccept}
            style={{
              height: '48px',
              fontSize: '16px',
            }}
          >
            {acceptSessionTransferLoading ? <Spinner size="sm" /> : 'Accept'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SessionAcceptReject;
